import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import Immutable from 'immutable';
import InformationOverlay from '../../../components/InformationOverlay';
import DiscountSummaryList from '../../../components/DiscountSummaryList';
import CouponOverlay from '../CouponOverlay';
import { getProductDiscountTiers } from '../helpers';

import './style.scss';

const CartSummary = ({ showProducts }) => {
  const { t } = useTranslation();
  const commerce = useSelector((state) => state.get('commerce'));
  const totalNet = commerce.getIn([
    'cart',
    'content',
    'attributes',
    'order_total',
    'subtotal',
    'formatted',
  ]);
  const totalWithTax = commerce.getIn([
    'cart',
    'content',
    'attributes',
    'total_price',
    'formatted',
  ]);

  const discountTiers = getProductDiscountTiers(
    commerce.getIn(['cart', 'products'], Immutable.List()).get(0),
  );

  const rawAdjustments = commerce.getIn(
    ['cart', 'content', 'attributes', 'order_total', 'adjustments'],
    Immutable.List(),
  );

  // Merge multiple promotion adjustments into a single row
  const promotionAdjustments = rawAdjustments.filter(
    (adj) => adj.get('type') === 'promotion',
  );
  const hasPromotion = promotionAdjustments.size > 0;

  // The discount overlay differs by source: a coupon-driven promotion shows the
  // coupon detail; a volume discount (staffelkorting) shows the tier breakdown.
  // Drive this off the is_coupon flag on the order-item adjustments (the
  // order_total adjustments don't carry it), not the quantity.
  const showApplyCouponField = commerce
    .getIn(['cart', 'products'], Immutable.List())
    .some((product) => product
      .getIn(['attributes', 'adjustments'], Immutable.List())
      .some((adj) => adj.get('type') === 'promotion' && adj.get('is_coupon')),
    );
  const promotionTotalNumber = promotionAdjustments.reduce(
    (sum, adj) => sum + parseFloat(adj.getIn(['total', 'number'], 0)),
    0,
  );
  const promotionFormatted = hasPromotion
    ? `€ ${promotionTotalNumber.toFixed(2).replace('.', ',')}`
    : null;
  const nonPromotionAdjustments = rawAdjustments.filter(
    (adj) => adj.get('type') !== 'promotion',
  );

  return (
    <>
      {showProducts &&
        commerce
          .getIn(['cart', 'products'], Immutable.List())
          .map((product) => {
            const productTotalPrice = parseFloat(
              product.getIn(['attributes', 'total_price', 'number'], 0),
            );
            const priceDisplay =
              productTotalPrice === 0
                ? t('cart.free')
                : product.getIn(['attributes', 'total_price', 'formatted']);
            const productTitle = product.getIn(
              [
                'relationships',
                'purchased_entity',
                'relationships',
                'product_id',
                'attributes',
                'title',
              ],
              product.getIn(['attributes', 'title'], ''),
            );

            return (
              <div key={product.get('id')} className="CartPopup__product">
                <div className="CartPopup__product__titlerow">
                  <div>
                    <strong>
                      {parseInt(product.getIn(['attributes', 'quantity'], 1))}x{' '}
                      {productTitle}
                    </strong>
                  </div>
                  <div>{priceDisplay}</div>
                </div>
              </div>
            );
          })}

      <div className="CartSummary__product__titlerow">
        <div>{t('cart.orderValue')}</div>
        <div>{totalNet}</div>
      </div>

      {hasPromotion && (
        <div className="CartSummary__product__titlerow">
          <div className="CartPopup__product__seats">
            {t('cart.discount')}
            {showApplyCouponField ? (
              <CouponOverlay
                products={commerce.getIn(
                  ['cart', 'products'],
                  Immutable.List(),
                )}
              />
            ) : (
              discountTiers.length > 0 && (
                <InformationOverlay
                  overlayKey="CartSummary__discount"
                  title={t('cart.discount')}
                >
                  <DiscountSummaryList
                    products={commerce.getIn(
                      ['cart', 'products'],
                      Immutable.List(),
                    )}
                  />
                </InformationOverlay>
              )
            )}
          </div>
          <div>{promotionFormatted}</div>
        </div>
      )}

      {nonPromotionAdjustments.map((adjustment) => {
        switch (adjustment.get('type')) {
          case 'shipping':
            return (
              <div
                key={adjustment.get('type')}
                className="CartSummary__product__titlerow"
              >
                <div>{t('cart.shippingCost')}</div>
                <div>{adjustment.getIn(['total', 'formatted'])}</div>
              </div>
            );
          case 'tax':
            return (
              <div
                key={adjustment.get('type')}
                className="CartSummary__product__titlerow"
              >
                <div>{t('cart.VAT')}</div>
                <div>{adjustment.getIn(['total', 'formatted'])}</div>
              </div>
            );
          default:
            break;
        }
      })}

      <hr />

      <div className="CartSummary__product__titlerow">
        <div>
          <strong>{t('cart.totalWithVAT')}</strong>
        </div>
        <div>
          <strong>{totalWithTax}</strong>
        </div>
      </div>
    </>
  );
};

export default CartSummary;
